import { CircularProgress, MenuItem, Select, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import useFetch from '../../hooks/useFetch'
import { urlApi } from '../../config'
import { ArrowBackIosNew } from '@mui/icons-material'
import Alert from '../../common/Alert'
import { formatDateTimeInUruguay, calculateAge, fixPhoneNumber } from './utils'
import Messages from '../Messages'
import UserDetailsCard from './UserDetailsCard'
import UserMetadataCard from './UserMetadataCard'
import UserStats from './UserStats'
import UserLobbies from './UserLobbies'
import UserRatings from './UserRatings'
import UserBankAccounts from './UserBankAccounts'
import UserTransactions from './UserTransactions'
import UserWallet from './UserWallet'
import { useUserWallet, walletBalanceLabels } from './wallet'
import WhatsAppComposer from '../../common/WhatsAppComposer'
import {
  VerifyUser,
  DeclineVerification,
  BlockUser,
  UnblockUser,
  SendComment,
  DeletePhoneNumber
} from './UserActions'
import usePermissions from '../../hooks/usePermissions'

export default function UserInfo({ userInfo, setUserInfo }) {
  const { data, loading, responseOk } = useFetch(
    `${urlApi}/admin/users/id/${userInfo.user_id}`
  )

  if (loading || !responseOk) {
    return (
      <div>
        <ArrowBackIosNew className='cursor-pointer mt-3' onClick={() => setUserInfo()} />
        <div className='flex justify-center items-center py-16'>
          {loading ? (
            <CircularProgress />
          ) : (
            <p className='text-error font-bold'>No se pudo cargar la información del usuario</p>
          )}
        </div>
      </div>
    )
  }

  return <UserInfoLoaded userInfo={data} onBack={() => setUserInfo()} />
}

function UserInfoLoaded({ userInfo, onBack }) {
  useEffect(() => {
    if (userInfo.user_metadata.first_name) {
      userInfo.user_metadata.first_name = userInfo.user_metadata.first_name.trim()
    }
    if (userInfo.user_metadata.phone_number) {
      fixPhoneNumber(userInfo)
    }
  }, [userInfo])

  const created = formatDateTimeInUruguay(userInfo.created)
  const lastLogin = formatDateTimeInUruguay(userInfo.last_login)

  const [userLobbiesVisible, setUserLobbiesVisible] = useState(false)
  const [verifyUserAlert, setVerifyUserAlert] = useState(false)
  const [verifyUser, setVerifyUser] = useState(false)
  const [declineVerificationAlert, setDeclineVerificationAlert] = useState(false)
  const [declineVerification, setDeclineVerification] = useState(false)
  const [declineReason, setDeclineReason] = useState('profile-picture')
  const [declineReasonText, setDeclineReasonText] = useState('')
  const [blockUserAlert, setBlockUserAlert] = useState(false)
  const [blockUser, setBlockUser] = useState(false)
  const [unblockUserAlert, setUnblockUserAlert] = useState(false)
  const [unblockUser, setUnblockUser] = useState(false)
  const [deletePhoneNumberAlert, setDeletePhoneNumberAlert] = useState(false)
  const [deletePhoneNumber, setDeletePhoneNumber] = useState(false)
  const [commentAlert, setCommentAlert] = useState(false)
  const [comment, setComment] = useState(userInfo.user_metadata.admin_comment || '')
  const [sendComment, setSendComment] = useState(false)
  const [bankAccountsVisible, setBankAccountsVisible] = useState(false)
  const [transactionsVisible, setTransactionsVisible] = useState(false)
  const [ratingsVisible, setRatingsVisible] = useState(false)
  const [chatsVisible, setChatsVisible] = useState(false)
  const [whatsAppVisible, setWhatsAppVisible] = useState(false)
  const age = calculateAge(userInfo.user_metadata.birthdate)
  const { hasPermission, hasAnyPermission } = usePermissions()
  const canEditUser = hasPermission('edit-user')
  const canReadUser = hasPermission('read-user')
  const userWallet = useUserWallet(userInfo.user_id, canReadUser)
  const [walletVisible, setWalletVisible] = useState(false)

  const handleShowWallet = () => {
    setWalletVisible(true)
    userWallet.reload()
  }

  const canSendWhatsApp =
    hasAnyPermission('edit-user', 'channels') && Boolean(userInfo.user_metadata.phone_number)

  return (
    <div>
      <ArrowBackIosNew className='cursor-pointer mt-3' onClick={onBack} />
      <div className='flex flex-col md:flex-row gap-3 pt-6'>
        <UserDetailsCard
          userInfo={userInfo}
          created={created}
          lastLogin={lastLogin}
          onBlockUser={canEditUser ? () => setBlockUserAlert(true) : null}
          onUnblockUser={canEditUser ? () => setUnblockUserAlert(true) : null}
          onComment={canEditUser ? () => setCommentAlert(true) : null}
          onDeletePhoneNumber={canEditUser ? () => setDeletePhoneNumberAlert(true) : null}
          onSendWhatsApp={canSendWhatsApp ? () => setWhatsAppVisible(true) : null}
          comment={comment}
        />
        <UserMetadataCard
          userInfo={userInfo}
          age={age}
          onShowUserLobbies={() => setUserLobbiesVisible(true)}
          onShowBankAccounts={() => setBankAccountsVisible(true)}
          onShowTransactions={() => setTransactionsVisible(true)}
          onShowRatings={() => setRatingsVisible(true)}
          onShowChats={() => setChatsVisible(true)}
          onShowWallet={canReadUser ? handleShowWallet : null}
          walletBalances={walletBalanceLabels(userWallet.wallet)}
          onSendWhatsApp={canSendWhatsApp ? () => setWhatsAppVisible(true) : null}
          onVerifyUser={canEditUser ? () => setVerifyUserAlert(true) : null}
          isVerified={userInfo.user_metadata.verified === true}
        />
      </div>
      <div className='mt-6'>
        <UserStats
          driverStats={userInfo.driver_stats}
          companionStats={userInfo.companion_stats}
          loading={false}
        />
      </div>
      {userLobbiesVisible && (
        <UserLobbies
          userLobbiesVisible={userLobbiesVisible}
          setUserLobbiesVisible={setUserLobbiesVisible}
          userId={userInfo.user_id}
        />
      )}
      {bankAccountsVisible && (
        <UserBankAccounts
          bankAccountsVisible={bankAccountsVisible}
          setBankAccountsVisible={setBankAccountsVisible}
          bankAccounts={userInfo.user_metadata.bank_accounts}
        />
      )}
      {transactionsVisible && (
        <UserTransactions
          transactionsVisible={transactionsVisible}
          setTransactionsVisible={setTransactionsVisible}
          userId={userInfo.user_id}
        />
      )}
      {walletVisible && (
        <UserWallet
          visible={walletVisible}
          onClose={() => setWalletVisible(false)}
          userId={userInfo.user_id}
          wallet={userWallet.wallet}
          loading={userWallet.loading}
          error={userWallet.error}
          reload={userWallet.reload}
        />
      )}
      {ratingsVisible && (
        <UserRatings
          ratingsVisible={ratingsVisible}
          setRatingsVisible={setRatingsVisible}
          reviews={userInfo.user_metadata.reviews}
        />
      )}
      {chatsVisible && (
        <Messages
          visible={chatsVisible}
          setVisible={setChatsVisible}
          initialUserId={userInfo.user_id}
        />
      )}
      <WhatsAppComposer
        open={whatsAppVisible}
        onClose={() => setWhatsAppVisible(false)}
        user={{
          user_id: userInfo.user_id,
          first_name: userInfo.user_metadata.first_name,
          phone_number: userInfo.user_metadata.phone_number
        }}
      />
      {verifyUserAlert && (
        <Alert
          onSubmit={() => setVerifyUser(true)}
          onClose={() => setVerifyUserAlert(false)}
          visible={verifyUserAlert}
        >
          <p className='font-bold'>Verificar usuario</p>
        </Alert>
      )}
      {verifyUser && <VerifyUser setVerifyUser={setVerifyUser} userId={userInfo.user_id} />}
      {declineVerificationAlert && (
        <Alert
          onSubmit={() => setDeclineVerification(true)}
          onClose={() => setDeclineVerificationAlert(false)}
          visible={declineVerificationAlert}
          disabled={!declineReason || (declineReason === 'other' && !declineReasonText)}
        >
          <div className='flex flex-col gap-6 mb-6'>
            <p>¿Seguro que querés rechazar la verificación del usuario?</p>
            <Select
              value={declineReason}
              size='small'
              placeholder='Seleccionar motivo'
              onChange={evt => setDeclineReason(evt.target.value)}
            >
              <MenuItem value='profile-picture'>Falta foto de perfil</MenuItem>
              <MenuItem value='image'>Imagen de documento poco clara / invalida</MenuItem>
              <MenuItem value='profile-name'>Falta nombre en perfil</MenuItem>
              <MenuItem value='profile-contact'>Info de contacto en perfil</MenuItem>
              <MenuItem value='face-picture'>
                Falta la foto donde se vea tu rostro junto al documento
              </MenuItem>
              <MenuItem value='other'>Otro</MenuItem>
            </Select>
            {declineReason === 'other' && (
              <TextField
                label='Motivo'
                variant='outlined'
                value={declineReasonText}
                onChange={e => setDeclineReasonText(e.target.value)}
              />
            )}
          </div>
        </Alert>
      )}
      {declineVerification && (
        <DeclineVerification
          setDeclineVerification={setDeclineVerification}
          userId={userInfo.user_id}
          reason={declineReason === 'other' ? declineReasonText : declineReason}
        />
      )}
      {blockUserAlert && (
        <Alert
          onSubmit={() => setBlockUser(true)}
          onClose={() => setBlockUserAlert(false)}
          visible={blockUserAlert}
        >
          <p className='font-bold'>Bloquear usuario</p>
        </Alert>
      )}
      {blockUser && (
        <BlockUser
          setBlockUser={setBlockUser}
          userId={userInfo.user_id}
          handleSuccess={() => (userInfo.blocked = true)}
        />
      )}
      {unblockUserAlert && (
        <Alert
          onSubmit={() => setUnblockUser(true)}
          onClose={() => setUnblockUserAlert(false)}
          visible={unblockUserAlert}
        >
          <p className='font-bold'>Desbloquear usuario</p>
        </Alert>
      )}
      {unblockUser && (
        <UnblockUser
          setUnblockUser={setUnblockUser}
          userId={userInfo.user_id}
          handleSuccess={() => (userInfo.blocked = false)}
        />
      )}
      {deletePhoneNumberAlert && (
        <Alert
          onSubmit={() => setDeletePhoneNumber(true)}
          onClose={() => setDeletePhoneNumberAlert(false)}
          visible={deletePhoneNumberAlert}
        >
          <p className='font-bold'>Eliminar número de teléfono</p>
          <p className='text-sm text-gray dark:text-gray-400'>Esto permitirá al usuario registrarse con otra cuenta</p>
        </Alert>
      )}
      {deletePhoneNumber && (
        <DeletePhoneNumber setDeletePhoneNumber={setDeletePhoneNumber} userId={userInfo.user_id} />
      )}
      {commentAlert && (
        <Alert
          onClose={() => {
            setComment(userInfo.user_metadata.admin_comment || '')
            setCommentAlert(false)
          }}
          onSubmit={() => setSendComment(true)}
          visible={commentAlert}
          onlyConfirm
          disabled={sendComment}
        >
          <div className='flex flex-col mb-6'>
            <TextField
              label='Comentario'
              variant='outlined'
              value={comment}
              multiline
              onChange={e => setComment(e.target.value)}
              onKeyDown={e => {
                if (e.key !== 'Enter' || e.shiftKey) return
                e.preventDefault()
                if (!sendComment) setSendComment(true)
              }}
            />
            <p className='font-bold text-xs text-gray text-start mt-2 ml-1 dark:text-gray-400'>
              Shift+Enter para nueva linea
            </p>
          </div>
        </Alert>
      )}
      {sendComment && (
        <SendComment
          setSendComment={setSendComment}
          userId={userInfo.user_id}
          comment={comment}
          handleSuccess={() => {
            userInfo.user_metadata.admin_comment = comment
            setCommentAlert(false)
          }}
        />
      )}
    </div>
  )
}
